import * as React from "react";
import { Outlet, useNavigate } from "react-router-dom";
import AdminSidebar from "./AdminSidebar";
import AdminHeader from "./AdminHeader";
import { useClearCacheMutation } from "../../redux/actions/Admin/ServiceAdmin";
import { CLEAR_ALL_CACHE } from "../../config/apiUrlsAdmin";
import { useAlert } from "../../components/customer/AlertContext";

type DashboardViewMode = "day" | "week" | "month";

type DashboardFilterMode = "day" | "week" | "month" | "custom";

type DashboardDateRange = {
  fromDate?: string;
  toDate?: string;
};

const AdminMainLayout: React.FC = () => {
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);

  // ============================================================
  // VIEW MODE
  //
  // Used by dashboard UI/chart presentation.
  // Custom ranges are still represented by their actual dates,
  // therefore custom does not need to be a view mode.
  // ============================================================

  const [viewMode, setViewMode] = React.useState<DashboardViewMode>("week");

  const [filterMode, setFilterMode] =
    React.useState<DashboardFilterMode>("week");

  const [headerDates, setHeaderDates] = React.useState<{
    fromDate?: string;
    toDate?: string;
  }>({});

  const [isSigningOut, setIsSigningOut] = React.useState(false);

  const navigate = useNavigate();

  const [clearCache, { isLoading: isClearingCache }] = useClearCacheMutation();

  const { showConfirm } = useAlert();

  // ============================================================
  // SIDEBAR
  // ============================================================

  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  // ============================================================
  // CLEAR SESSION
  // ============================================================

  const clearSessionDataAndCookies = async () => {
    localStorage.clear();
    sessionStorage.clear();

    const cookies = document.cookie.split(";");

    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i];

      const eqPos = cookie.indexOf("=");

      const name =
        eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();

      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;`;

      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname};`;
    }

    if ("caches" in window) {
      try {
        const cacheKeys = await caches.keys();

        await Promise.all(cacheKeys.map((key) => caches.delete(key)));
      } catch (err) {
        console.error("Failed to clear browser cache storage:", err);
      }
    }
  };

  // ============================================================
  // SIGN OUT
  // ============================================================

  const handleSignOut = () => {
    showConfirm({
      title: "Sign Out",

      message:
        "Are you sure you want to sign out? Your session cache and storage will be cleared.",

      type: "warning",

      confirmText: "Sign Out",

      cancelText: "Cancel",

      showCancel: true,

      onConfirm: async () => {
        try {
          setIsSigningOut(true);

          await clearCache(CLEAR_ALL_CACHE)
            .unwrap()
            .catch(() => {});

          await clearSessionDataAndCookies();

          navigate("/admin/login", { replace: true });
        } finally {
          setIsSigningOut(false);
        }
      },
    });
  };

  // ============================================================
  // CLEAR CACHE
  // ============================================================

  const handleClearAllCache = () => {
    showConfirm({
      title: "Clear All Cache",

      message: "Are you sure you want to clear all application caches?",

      type: "warning",

      confirmText: "Clear All",

      cancelText: "Dismiss",

      showCancel: true,

      onConfirm: async () => {
        try {
          await clearCache(CLEAR_ALL_CACHE).unwrap();

          showConfirm({
            title: "Success",

            message: "All application caches cleared successfully.",

            type: "success",

            confirmText: "OK",

            showCancel: false,
          });
        } catch {
          showConfirm({
            title: "Error",

            message: "Unable to clear cache. Please try again.",

            type: "error",

            confirmText: "OK",

            showCancel: false,
          });
        }
      },
    });
  };

  // ============================================================
  // FILTER CHANGE
  // ============================================================

  const handleFilterChange = (filterData: {
    mode: DashboardFilterMode;
    displayLabel: string;
    value: {
      raw: any;
      fromDate?: string;
      toDate?: string;
    };
  }) => {
    console.log("ADMIN FILTER RECEIVED:", filterData);

    // Keep the actual filter mode.
    setFilterMode(filterData.mode);

    // Custom is not a view mode.
    if (
      filterData.mode === "day" ||
      filterData.mode === "week" ||
      filterData.mode === "month"
    ) {
      setViewMode(filterData.mode);
    }

    setHeaderDates({
      fromDate: filterData.value?.fromDate,

      toDate: filterData.value?.toDate,
    });
  };

  return (
    <div className="bg-white min-h-screen">
      {/* ======================================================
          SIDEBAR
      ====================================================== */}

      <AdminSidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />

      {/* ======================================================
          HEADER
      ====================================================== */}

      <AdminHeader
        toggleSidebar={toggleSidebar}
        onFilterChange={handleFilterChange}
        onClearCache={handleClearAllCache}
        onSignOut={handleSignOut}
        isClearingCache={isClearingCache}
        isSigningOut={isSigningOut}
      />

      {/* ======================================================
          MAIN CONTENT
      ====================================================== */}

      <main className="pt-[90px] px-4 md:ml-[260px]">
        <Outlet
          context={{
            viewMode,
            filterMode,
            headerDates,
          }}
        />
      </main>
    </div>
  );
};

export default AdminMainLayout;
